import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Stack,
  Text,
  Button,
  Paper,
  Group,
  Box,
  Center,
  Skeleton,
  Badge,
  Flex,
  Pagination,
  Alert,
  ActionIcon,
  Tabs,
  SegmentedControl,
} from '@mantine/core';
import { ResponsiveDrawer, useIsBottomSheet } from '../UI/ResponsiveDrawer';
import {
  IconBook,
  IconClock,
  IconUsers,
  IconFilter,
  IconWand,
  IconX,
  IconCheck,
  IconArchive,
} from '@tabler/icons-react';
import { DrawerHeader } from '../UI/DrawerHeader';
import { notifications } from '@mantine/notifications';
import { Recipe, MealPlan, MealType } from '../../types/mealPlan';
import { getAssignmentRecipe } from '../../utils/mealPlanHelpers';
import { MEAL_SLOT_MATCHERS } from '../../utils/mealSlotMatchers';
import { RecipeSearchFilters } from '../Recipe/RecipeSearchFilters';
import { RecipeImage } from '../UI/RecipeImage';
import { TopPickBadge } from '../UI/TopPickRibbon';
import { formatTime } from '../../utils/formatTime';
import { useAuth } from '../../hooks/useAuth';
import { fetchRecipeList, getCachedRecipeList } from '../../utils/recipeListCache';
import { getRecipeBadges, splitBadgesForDisplay } from '../../utils/recipeBadges';
import { MoreRestrictionsMenu } from '../UI/MoreRestrictionsMenu';
import { HouseholdSegment } from '../../utils/householdSegment';
import { CuisineBadge } from '../UI/CuisineBadge';
import { formatCount } from '../../utils/formatCount';

interface RecipeSelectionDrawerProps {
  opened: boolean;
  onClose: () => void;
  onSelectRecipe: (recipe: Recipe) => void;
  onRemixSubmission?: (day: string) => void;
  selectedDay: string;
  replacingRecipeName?: string;
  replacingRecipeId?: string;
  userRecipes: Recipe[];
  loading: boolean;
  mealPlan?: MealPlan;
  // Multi-select mode (wizard picker): taps toggle picks via onSelectRecipe,
  // the drawer stays open, and remix/archive are hidden (picks must be
  // cookbook recipes)
  multiSelect?: boolean;
  selectedRecipeIds?: string[];
  // Viewer context for the badge rows (utils/recipeBadges); omit when unknown
  userDietaryRestrictions?: string[];
  userHouseholdSegment?: HouseholdSegment;
  // The meal slot this selection is for. When provided, a meal-type filter is
  // shown, defaulted to this slot (one tap to widen back to all recipes).
  mealTypeContext?: MealType;
  // Steps back to the drawer this one was opened from (the swap drawer)
  onBack?: () => void;
}


export const RecipeSelectionDrawer = ({
  opened,
  onClose,
  onBack,
  onSelectRecipe,
  onRemixSubmission,
  selectedDay,
  replacingRecipeName,
  replacingRecipeId,
  userRecipes,
  loading,
  mealPlan,
  multiSelect = false,
  selectedRecipeIds = [],
  userDietaryRestrictions,
  userHouseholdSegment,
  mealTypeContext,
}: RecipeSelectionDrawerProps) => {
  const { user } = useAuth();
  const isBottomSheet = useIsBottomSheet();
  
  // Source selection state
  const [recipeSource, setRecipeSource] = useState<'cookbook' | 'archive'>('cookbook');
  // Seeded from the session's shared archive list (also the Recipe Archive
  // page's cache) so the tab renders instantly and refreshes silently
  const [archiveRecipes, setArchiveRecipes] = useState<Recipe[]>(
    () => getCachedRecipeList('archive', user?.uid) ?? []
  );
  const [loadingArchive, setLoadingArchive] = useState(false);
  const hasArchiveListRef = useRef(!!getCachedRecipeList('archive', user?.uid));
  // Revalidate once per drawer opening, on the first switch to the tab
  const archiveRevalidatedRef = useRef(false);
  useEffect(() => {
    if (!opened) archiveRevalidatedRef.current = false;
  }, [opened]);
  
  // Meal-type filter, defaulted to the slot being filled each time the drawer
  // opens ('all' when no slot context)
  const [selectedMealCategory, setSelectedMealCategory] = useState<'all' | MealType>('all');
  useEffect(() => {
    if (opened) {
      setSelectedMealCategory(mealTypeContext ?? 'all');
    }
  }, [opened, mealTypeContext]);

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCuisines, setSelectedCuisines] = useState<string[]>([]);
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>('');
  const [selectedDietaryRestrictions, setSelectedDietaryRestrictions] = useState<string[]>([]);
  const [selectedPrepTime, setSelectedPrepTime] = useState<string>('');
  const [selectedTotalTime, setSelectedTotalTime] = useState<string>('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const recipesPerPage = 10;
  const firstRecipeRef = useRef<HTMLDivElement>(null);
  const hasInitiallyLoadedRef = useRef(false);
  const previousPageRef = useRef(1);
  
  // Remix functionality state
  const [remixMode, setRemixMode] = useState(false);
  const [selectedRecipes, setSelectedRecipes] = useState<Set<string>>(new Set());
  const [isRemixing, setIsRemixing] = useState(false);

  // Fetch archive recipes
  const fetchArchiveRecipes = useCallback(async () => {
    if (!user) return;

    setLoadingArchive(!hasArchiveListRef.current);
    try {
      setArchiveRecipes(await fetchRecipeList(user, 'archive'));
      hasArchiveListRef.current = true;
    } catch (error: any) {
      console.error('Error fetching archive recipes:', error);
      // A failed background refresh keeps the list on screen
      if (hasArchiveListRef.current) return;
      notifications.show({
        title: 'Error',
        message: 'Failed to load archive recipes. Please try again.',
        color: 'red',
      });
    } finally {
      setLoadingArchive(false);
    }
  }, [user]);

  // Fetch archive recipes when switching to archive source
  useEffect(() => {
    if (recipeSource === 'archive' && !archiveRevalidatedRef.current) {
      archiveRevalidatedRef.current = true;
      fetchArchiveRecipes();
    }
    // Clear remix selections when switching to archive (since remix is not allowed)
    if (recipeSource === 'archive' && selectedRecipes.size > 0) {
      setSelectedRecipes(new Set());
      setRemixMode(false);
    }
  }, [recipeSource, opened, fetchArchiveRecipes, selectedRecipes.size]);

  // Get current recipe source
  const currentRecipes = recipeSource === 'cookbook' ? userRecipes : archiveRecipes;
  const currentLoading = recipeSource === 'cookbook' ? loading : loadingArchive;

  // Get unique values for filter options
  const cuisineOptions = useMemo(() => {
    const cuisines = new Set(currentRecipes.map(r => r.cuisine).filter(Boolean) as string[]);
    return Array.from(cuisines).sort();
  }, [currentRecipes]);

  const dietaryOptions = useMemo(() => {
    const restrictions = new Set<string>();
    currentRecipes.forEach(recipe => {
      recipe.dietaryRestrictions?.forEach(restriction => restrictions.add(restriction));
    });
    return Array.from(restrictions).sort();
  }, [currentRecipes]);

  // Filter recipes
  const filteredRecipes = useMemo(() => {
    return currentRecipes.filter(recipe => {
      // Meal-type filter
      if (
        selectedMealCategory !== 'all' &&
        !MEAL_SLOT_MATCHERS[selectedMealCategory](recipe)
      ) {
        return false;
      }

      // Search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = recipe.name.toLowerCase().includes(query) ||
          recipe.description?.toLowerCase().includes(query) ||
          recipe.cuisine?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }

      // Cuisine filter
      if (selectedCuisines.length > 0) {
        if (!recipe.cuisine || !selectedCuisines.includes(recipe.cuisine)) {
          return false;
        }
      }

      // Difficulty filter
      if (selectedDifficulty && recipe.difficulty !== selectedDifficulty) {
        return false;
      }

      // Prep time filter
      if (selectedPrepTime) {
        const prepTime = recipe.prepTimeMinutes;
        switch (selectedPrepTime) {
          case 'quick':
            if (prepTime > 15) return false;
            break;
          case 'medium':
            if (prepTime <= 15 || prepTime > 30) return false;
            break;
          case 'long':
            if (prepTime <= 30) return false;
            break;
        }
      }

      // Total time filter
      if (selectedTotalTime) {
        const totalTime = recipe.totalTimeMinutes;
        switch (selectedTotalTime) {
          case 'quick':
            if (totalTime > 30) return false;
            break;
          case 'medium':
            if (totalTime <= 30 || totalTime > 60) return false;
            break;
          case 'long':
            if (totalTime <= 60) return false;
            break;
        }
      }

      // Dietary restrictions filter
      if (selectedDietaryRestrictions.length > 0) {
        const recipeRestrictions = recipe.dietaryRestrictions || [];
        const hasAllRestrictions = selectedDietaryRestrictions.every(restriction =>
          recipeRestrictions.includes(restriction)
        );
        if (!hasAllRestrictions) return false;
      }

      return true;
    });
  }, [currentRecipes, searchQuery, selectedCuisines, selectedDifficulty, selectedPrepTime, selectedTotalTime, selectedDietaryRestrictions, selectedMealCategory]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredRecipes.length / recipesPerPage);
  const startIndex = (currentPage - 1) * recipesPerPage;
  const paginatedRecipes = filteredRecipes.slice(startIndex, startIndex + recipesPerPage);

  // Reset page when filters change or source changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCuisines, selectedDifficulty, selectedPrepTime, selectedTotalTime, selectedDietaryRestrictions, selectedMealCategory, recipeSource]);

  // Scroll to top when page changes (but not on initial load)
  useEffect(() => {
    const pageActuallyChanged = currentPage !== previousPageRef.current;
    
    if (firstRecipeRef.current && paginatedRecipes.length > 0 && hasInitiallyLoadedRef.current && pageActuallyChanged) {
      // Use requestAnimationFrame for better timing with DOM updates
      requestAnimationFrame(() => {
        firstRecipeRef.current?.scrollIntoView({ 
          behavior: 'auto', 
          block: 'center' // Use center to avoid being cut off by the title
        });
      });
    }
    
    // Update previous page ref
    previousPageRef.current = currentPage;
    
    // Mark as initially loaded after first render
    if (paginatedRecipes.length > 0 && !hasInitiallyLoadedRef.current) {
      hasInitiallyLoadedRef.current = true;
    }
  }, [currentPage, paginatedRecipes.length]); 

  const isRecipeBeingReplaced = (recipe: Recipe) => {
    return Boolean(replacingRecipeId && recipe.id === replacingRecipeId);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCuisines([]);
    setSelectedDifficulty('');
    setSelectedDietaryRestrictions([]);
    setSelectedPrepTime('');
    setSelectedTotalTime('');
    setSelectedMealCategory('all');
    setCurrentPage(1);
  };

  // Remix functionality handlers
  const handleToggleRemixMode = () => {
    setRemixMode(!remixMode);
    setSelectedRecipes(new Set());
  };

  const handleRecipeSelect = (recipeId: string, selected: boolean) => {
    setSelectedRecipes(prev => {
      const newSet = new Set(prev);
      if (selected && newSet.size < 3) {
        newSet.add(recipeId);
      } else if (!selected) {
        newSet.delete(recipeId);
      }
      return newSet;
    });
  };

  const handleRemixRecipes = async () => {
    if (selectedRecipes.size === 0) return;

    setIsRemixing(true);
    try {
      const token = await user?.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/recipes/remix`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recipeIds: Array.from(selectedRecipes),
          servings: (() => {
            const assignment = mealPlan?.assignments?.find(a => a.plannedDay === selectedDay);
            return assignment ? getAssignmentRecipe(assignment, mealPlan)?.servings : undefined;
          })(),
          mealPlanId: mealPlan?.id,
          dayToReplace: selectedDay
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to start recipe remix');
      }

      notifications.show({
        title: 'Recipe remix started',
        message: mealPlan?.id ? 'Creating fusion recipe and substituting into your meal plan...' : 'Your fusion recipe is being created! Check my cookbook in a few moments.',
        color: 'violet',
      });

      // Reset remix mode and close drawer
      setRemixMode(false);
      setSelectedRecipes(new Set());
      if (mealPlan?.id) {
        onClose(); // Close drawer when substituting into meal plan
        // Notify parent component to start polling
        onRemixSubmission?.(selectedDay);
      }
      
    } catch (error: any) {
      notifications.show({
        title: 'Remix failed',
        message: error.message || 'Failed to start recipe remix. Please try again.',
        color: 'red',
      });
    } finally {
      setIsRemixing(false);
    }
  };

  const handleRecipeSelection = (recipe: Recipe) => {
    onSelectRecipe(recipe);
    // In multi-select the drawer stays open, so keep the filters too
    if (!multiSelect) {
      clearFilters();
    }
  };

  const isRecipeSelected = (recipe: Recipe) =>
    multiSelect && selectedRecipeIds.includes(recipe.id);

  // Clear filters when drawer closes
  useEffect(() => {
    if (!opened) {
      clearFilters();
    }
  }, [opened]);

  return (
    <ResponsiveDrawer
      opened={opened}
      onClose={onClose}
      mobileSize="92%"
      title={
        <DrawerHeader
          onBack={onBack}
          backLabel="Back to swap options"
          icon={recipeSource === 'cookbook' ? <IconBook size={18} /> : <IconArchive size={18} />}
          title={recipeSource === 'cookbook' ? 'My cookbook' : 'Recipe archive'}
          rightSection={
            multiSelect ? undefined : (
            <Button
              size="sm"
              variant={remixMode ? "filled" : "light"}
              color="violet"
              leftSection={remixMode ? <IconX size={16} /> : <IconWand size={16} />}
              onClick={handleToggleRemixMode}
              disabled={currentRecipes.length === 0}
              style={{ visibility: recipeSource === 'cookbook' ? 'visible' : 'hidden' }}
            >
              {remixMode ? "Cancel" : "Remix"}
            </Button>
            )
          }
        />
      }
      size="xl"
    >
      <Stack gap="lg">
        {/* Context */}
        {replacingRecipeName && !remixMode && (
          <Paper p="md" bg="gray.0" withBorder radius="lg">
            <Group gap="sm" align="flex-start">
              <Text size="sm" c="dimmed" style={{ minWidth: 'fit-content' }}>
                Replacing:
              </Text>
              <Text size="sm" fw={500} style={{ flex: 1 }}>
                {replacingRecipeName}
              </Text>
            </Group>
            <Group gap="sm" align="flex-start" mt="xs">
              <Text size="sm" c="dimmed" style={{ minWidth: 'fit-content' }}>
                Day:
              </Text>
              <Text size="sm" fw={500}>
                {selectedDay}
              </Text>
            </Group>
          </Paper>
        )}

        {/* Recipe Source Tabs */}
        {!multiSelect && (
        <Tabs value={recipeSource} onChange={(value) => setRecipeSource(value as 'cookbook' | 'archive')}>
          <Tabs.List>
            <Tabs.Tab value="cookbook" leftSection={<IconBook size={16} />}>
              My cookbook
            </Tabs.Tab>
            <Tabs.Tab value="archive" leftSection={<IconArchive size={16} />}>
              Archive
            </Tabs.Tab>
          </Tabs.List>
        </Tabs>
        )}

        {/* Remix Mode Instructions */}
        {remixMode && (
          <Alert color="violet" variant="light" p="sm" radius="lg">
            <Group justify="space-between" align="center" gap="sm">
              <Text size="sm" c="violet.7" style={{ flex: 1 }}>
                Select 1-3 recipes to remix {recipeSource === 'archive' ? '(switch to my cookbook to remix)' : 'from cookbook'}
              </Text>
              <Button
                size="sm"
                color="violet"
                loading={isRemixing}
                disabled={selectedRecipes.size === 0}
                onClick={handleRemixRecipes}
                styles={selectedRecipes.size === 0 ? {
                  root: {
                    borderColor: 'var(--mantine-color-gray-4)',
                    backgroundColor: 'transparent',
                    color: 'var(--mantine-color-gray-6)'
                  }
                } : undefined}
              >
                Remix ({selectedRecipes.size})
              </Button>
            </Group>
          </Alert>
        )}

        {/* Meal-type filter, shown when the drawer knows which slot it's
            filling; defaults to that slot */}
        {mealTypeContext && (
          <SegmentedControl
            value={selectedMealCategory}
            onChange={(value) => setSelectedMealCategory(value as 'all' | MealType)}
            data={[
              { label: 'All', value: 'all' },
              { label: 'Breakfast', value: 'breakfast' },
              { label: 'Lunch', value: 'lunch' },
              { label: 'Dinner', value: 'dinner' },
            ]}
            size="xs"
            radius="md"
          />
        )}

        {/* Search and Filters */}
        <RecipeSearchFilters
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          selectedCuisines={selectedCuisines}
          selectedDifficulty={selectedDifficulty}
          selectedDietaryRestrictions={selectedDietaryRestrictions}
          selectedPrepTime={selectedPrepTime}
          selectedTotalTime={selectedTotalTime}
          cuisineOptions={cuisineOptions}
          dietaryOptions={dietaryOptions}
          onSelectedCuisinesChange={setSelectedCuisines}
          onSelectedDifficultyChange={setSelectedDifficulty}
          onSelectedDietaryRestrictionsChange={setSelectedDietaryRestrictions}
          onSelectedPrepTimeChange={setSelectedPrepTime}
          onSelectedTotalTimeChange={setSelectedTotalTime}
          onClearFilters={clearFilters}
          showCollapsible={true}
          showNeedsReviewFilter={false}
          showClearAllButton={true}
          searchPlaceholder="Search recipes by name, description, or cuisine..."
        />

        {/* Results */}
        <Stack gap="sm">
          <Group justify="space-between" align="center">
            {currentLoading ? (
              <Skeleton height={20} width={160} />
            ) : (
              <Text size="sm" c="dimmed">
                {filteredRecipes.length === 0 ? 'No recipes found' :
                  filteredRecipes.length <= recipesPerPage ?
                    `${formatCount(filteredRecipes.length)} recipe${filteredRecipes.length !== 1 ? 's' : ''} found` :
                    `Showing ${formatCount(startIndex + 1)}-${formatCount(Math.min(startIndex + recipesPerPage, filteredRecipes.length))} of ${formatCount(filteredRecipes.length)} recipes`
                }
              </Text>
            )}
            {currentLoading ? (
              <Skeleton height={20} width={60} />
            ) : (
              <Group gap="xs">
                <IconFilter size={14} />
                <Text size="xs" c="dimmed">
                  {formatCount(currentRecipes.length)} total
                </Text>
              </Group>
            )}
          </Group>

          {/* Recipe List */}
          <Box>
            {currentLoading ? (
              <Stack gap="sm">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Paper key={i} p="md" withBorder radius="lg">
                    <Stack gap="sm">
                      <Flex gap="md">
                        <Skeleton w={80} h={80} radius="md" style={{ flexShrink: 0 }} />
                        <Stack gap="xs" style={{ flex: 1 }}>
                          <Skeleton height={20} width="65%" />
                          <Skeleton height={14} width="90%" />
                          <Group gap="xs">
                            <Skeleton height={14} width={70} />
                            <Skeleton height={14} width={80} />
                            <Skeleton height={18} width={50} radius="xl" />
                          </Group>
                          <Group gap="xs">
                            <Skeleton height={18} width={55} radius="xl" />
                            <Skeleton height={18} width={65} radius="xl" />
                          </Group>
                        </Stack>
                      </Flex>
                      {/* Mobile select button skeleton */}
                      <Skeleton height={36} width="100%" radius="md" hiddenFrom="sm" />
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            ) : filteredRecipes.length === 0 ? (
              <Center py="xl">
                <Text size="sm" c="dimmed">
                  {currentRecipes.length === 0
                    ? (recipeSource === 'cookbook' ? 'No recipes in your cookbook yet.' : 'No archived recipes found.')
                    : selectedMealCategory !== 'all'
                      ? `No ${selectedMealCategory} recipes found. Switch the meal filter to All to see everything.`
                      : 'No recipes match your filters.'}
                </Text>
              </Center>
            ) : (
              <Stack gap="sm">
                {paginatedRecipes.map((recipe, index) => {
                  const isInMealPlan = mealPlan?.assignments?.some(assignment =>
                    getAssignmentRecipe(assignment, mealPlan)?.id === recipe.id
                  ) || false;

                  return (
                    <Paper
                      key={recipe.id}
                      ref={index === 0 ? firstRecipeRef : undefined}
                      p="md"
                      withBorder
                      radius="lg"
                      style={{
                        transition: 'all 0.2s ease',
                        opacity: isInMealPlan ? 0.7 : 1,
                        position: 'relative',
                        cursor: remixMode ?
                          (!selectedRecipes.has(recipe.id) && selectedRecipes.size >= 3 ? 'not-allowed' : 'pointer') :
                          undefined,
                        backgroundColor: remixMode && selectedRecipes.has(recipe.id)
                          ? 'var(--mantine-color-violet-0)'
                          : isInMealPlan ? 'var(--mantine-color-gray-0)' : 'white',
                        borderColor: remixMode && selectedRecipes.has(recipe.id)
                          ? 'var(--mantine-color-violet-3)'
                          : undefined
                      }}
                      onClick={remixMode ? () => handleRecipeSelect(recipe.id, !selectedRecipes.has(recipe.id)) : undefined}
                      className="recipe-card"
                    >
                      <Stack gap="sm">
                        <Flex gap="md">
                          {/* Recipe Image */}
                          <Box 
                            w={80} 
                            h={80}
                            style={{ 
                              flexShrink: 0,
                              borderRadius: 'var(--mantine-radius-md)',
                              overflow: 'hidden',
                              backgroundColor: 'var(--mantine-color-gray-1)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: remixMode ? 'inherit' : 'default',
                              position: 'relative'
                            }}
                          >
                            <RecipeImage
                              src={recipe.imageUrl}
                              alt={recipe.name}
                              width="100%"
                              height="100%"
                              fallbackIconSize={32}
                            />
                            {/* Recipe Selection Indicator (overlaid on image in remix mode) */}
                            {remixMode && (
                              <ActionIcon
                                style={{
                                  position: 'absolute',
                                  top: 8,
                                  left: 8,
                                  zIndex: 10,
                                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                                  opacity: selectedRecipes.has(recipe.id) ? 0.9 : 0.8
                                }}
                                variant={selectedRecipes.has(recipe.id) ? "filled" : "white"}
                                color={selectedRecipes.has(recipe.id) ? "violet" : "gray"}
                                size="sm"
                                radius="xl"
                                disabled={!selectedRecipes.has(recipe.id) && selectedRecipes.size >= 3}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleRecipeSelect(recipe.id, !selectedRecipes.has(recipe.id));
                                }}
                              >
                                {selectedRecipes.has(recipe.id) && <IconCheck size={16} />}
                              </ActionIcon>
                            )}
                          </Box>

                          {/* Recipe Content */}
                          <Stack gap="xs" style={{ flex: 1 }}>
                            <Text fw={500} size="lg" style={{ cursor: remixMode ? 'inherit' : 'default' }}>
                              {recipe.name}
                            </Text>

                            {recipe.description && (
                              <Text size="sm" c="dimmed" lineClamp={2} style={{ cursor: remixMode ? 'inherit' : 'default' }}>
                                {recipe.description}
                              </Text>
                            )}

                            {/* Time, servings, and key badges */}
                            <Group gap="sm">
                              <Group gap="xs">
                                <IconClock size={16} />
                                <Text size="sm">{formatTime(recipe.totalTimeMinutes)}</Text>
                              </Group>
                              <Group gap="xs">
                                <IconUsers size={16} />
                                <Text size="sm">{recipe.servings} {recipe.servings === 1 ? 'serving' : 'servings'}</Text>
                              </Group>
                              <Group gap="xs" visibleFrom="md">
                                {/* Both tabs are homogeneous (cookbook = all
                                    saved, archive = all unsaved), so a Cookbook
                                    marker would tag every row. Top pick shows
                                    only for unsaved (non-graduated) curated
                                    recipes, i.e. in the archive tab. */}
                                {recipe.isCurated &&
                                  !recipe.isSaved &&
                                  recipe.origin !== 'user_recipe' && <TopPickBadge />}
                                {recipe.difficulty && (
                                  <Badge 
                                    size="sm" 
                                    variant="light"
                                    color={recipe.difficulty === 'easy' ? 'green' : recipe.difficulty === 'medium' ? 'yellow' : 'red'}
                                    style={{ cursor: remixMode ? 'inherit' : 'default' }}
                                  >
                                    {recipe.difficulty}
                                  </Badge>
                                )}
                                {recipe.usageCount && recipe.usageCount > 0 && (
                                  <Badge size="sm" variant="light" color="blue" style={{ cursor: remixMode ? 'inherit' : 'default' }}>
                                    Used {recipe.usageCount} time{recipe.usageCount !== 1 ? 's' : ''}
                                  </Badge>
                                )}
                                {isInMealPlan && (
                                  <Badge size="sm" variant="light" color="blue" style={{ cursor: remixMode ? 'inherit' : 'default' }}>
                                    In Plan
                                  </Badge>
                                )}
                              </Group>
                            </Group>

                            {/* All badges for mobile */}
                            <Group gap="xs" hiddenFrom="md">
                              {recipe.isCurated &&
                                !recipe.isSaved &&
                                recipe.origin !== 'user_recipe' && <TopPickBadge />}
                              {recipe.difficulty && (
                                <Badge 
                                  size="sm" 
                                  variant="light"
                                  color={recipe.difficulty === 'easy' ? 'green' : recipe.difficulty === 'medium' ? 'yellow' : 'red'}
                                >
                                  {recipe.difficulty}
                                </Badge>
                              )}
                              {recipe.usageCount && recipe.usageCount > 0 && (
                                <Badge size="sm" variant="light" color="blue" style={{ cursor: remixMode ? 'inherit' : 'default' }}>
                                  Used {recipe.usageCount} time{recipe.usageCount !== 1 ? 's' : ''}
                                </Badge>
                              )}
                              {isInMealPlan && (
                                <Badge size="sm" variant="light" color="blue" style={{ cursor: remixMode ? 'inherit' : 'default' }}>
                                  In Plan
                                </Badge>
                              )}
                              {recipe.cuisine && (
                                <CuisineBadge cuisine={recipe.cuisine} style={{ cursor: remixMode ? 'inherit' : 'default' }} />
                              )}
                              {(() => {
                                const allBadges = getRecipeBadges(recipe, {
                                  dietaryRestrictions: userDietaryRestrictions,
                                  householdSegment: userHouseholdSegment,
                                });

                                const { visible, hiddenRestrictions } = splitBadgesForDisplay(allBadges, 3);

                                return (
                                  <>
                                    {visible.map((badge) => (
                                      <Badge key={`${badge.type}-${badge.text}`} size="sm" variant="outline" style={{ cursor: remixMode ? 'inherit' : 'default' }}>
                                        {badge.text}
                                      </Badge>
                                    ))}
                                    <MoreRestrictionsMenu restrictions={hiddenRestrictions} withinPortal={!isBottomSheet} />
                                  </>
                                );
                              })()}
                            </Group>

                            {/* Descriptive badges for desktop only */}
                            <Group gap="xs" visibleFrom="md">
                              {recipe.cuisine && (
                                <CuisineBadge cuisine={recipe.cuisine} style={{ cursor: remixMode ? 'inherit' : 'default' }} />
                              )}
                              {(() => {
                                const allBadges = getRecipeBadges(recipe, {
                                  dietaryRestrictions: userDietaryRestrictions,
                                  householdSegment: userHouseholdSegment,
                                });

                                const { visible, hiddenRestrictions } = splitBadgesForDisplay(allBadges, 3);

                                return (
                                  <>
                                    {visible.map((badge) => (
                                      <Badge key={`${badge.type}-${badge.text}`} size="sm" variant="outline" style={{ cursor: remixMode ? 'inherit' : 'default' }}>
                                        {badge.text}
                                      </Badge>
                                    ))}
                                    <MoreRestrictionsMenu restrictions={hiddenRestrictions} withinPortal={!isBottomSheet} />
                                  </>
                                );
                              })()}
                            </Group>
                          </Stack>
                          
                          {/* Select Button - Tablet and Desktop */}
                          {!remixMode && (
                            <Button
                              size="sm"
                              variant={isRecipeSelected(recipe) ? 'filled' : 'light'}
                              color="orange"
                              onClick={() => handleRecipeSelection(recipe)}
                              disabled={isRecipeBeingReplaced(recipe)}
                              style={{
                                alignSelf: 'center',
                                // Fixed width so Pick/Picked toggling doesn't
                                // reflow the card content beside it
                                minWidth: multiSelect ? 92 : undefined,
                              }}
                              visibleFrom="sm"
                            >
                              {isRecipeBeingReplaced(recipe)
                                ? 'Current recipe'
                                : multiSelect
                                ? isRecipeSelected(recipe)
                                  ? 'Picked'
                                  : 'Pick'
                                : 'Select'}
                            </Button>
                          )}
                        </Flex>

                        {/* Select Button - Mobile only */}
                        {!remixMode && (
                          <Button
                            size="sm"
                            variant={isRecipeSelected(recipe) ? 'filled' : 'light'}
                            color="orange"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleRecipeSelection(recipe);
                            }}
                            disabled={isRecipeBeingReplaced(recipe)}
                            hiddenFrom="sm"
                            fullWidth
                          >
                            {isRecipeBeingReplaced(recipe)
                              ? 'Current recipe'
                              : multiSelect
                              ? isRecipeSelected(recipe)
                                ? 'Picked'
                                : 'Pick recipe'
                              : 'Select recipe'}
                          </Button>
                        )}
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </Box>

          {/* Pagination */}
          {!loading && filteredRecipes.length > recipesPerPage && (
            <Group justify="center" mt="xl" mb="md">
              <Pagination
                value={currentPage}
                onChange={setCurrentPage}
                total={totalPages}
                size="md"
                radius="md"
                color="orange"
              />
            </Group>
          )}
        </Stack>

        {/* Actions */}
        <Group justify="flex-end">
          <Button
            variant="subtle"
            onClick={onClose}
            size="md"
          >
            Cancel
          </Button>
        </Group>
      </Stack>
    </ResponsiveDrawer>
  );
};
